/**
 * @fileoverview Centralized environment variable loader and validator for the client app
 *
 * Validates required REACT_APP_* variables at startup and exposes a typed config.
 * Provides clear, actionable error messages if configuration is missing.
 * In development, it does not hard-fail to allow the UI to render on machines
 * without local .env setup. In production, CI/build should ensure env is set.
 *
 * Notes:
 * - CRA exposes only variables prefixed with REACT_APP_ to the browser.
 * - Server-only variables (e.g., VAPID_PRIVATE_KEY) are validated in their server context.
 */

import logger from '../utils/logger';

/**
 * Define the client-visible environment schema. Keep it minimal and explicit.
 */
const schema = /** @type {const} */ ({
  REACT_APP_SUPABASE_URL: { required: true, description: 'Supabase project URL' },
  REACT_APP_SUPABASE_ANON_KEY: { required: true, description: 'Supabase anon public key' },
  REACT_APP_N8N_WEBHOOK_URL: {
    required: false,
    description: 'Override for chat webhook URL (optional; falls back to proxy or default)',
  },
  REACT_APP_N8N_FRONTEND_WEBHOOK_URL: {
    required: false,
    description: 'Override for generic frontend events webhook URL (optional)',
  },
  REACT_APP_VAPID_PUBLIC_KEY: {
    required: false,
    description: 'Public VAPID key for Web Push (required if push notifications are used)',
  },
  REACT_APP_ACCESS_REQUEST_EMAIL: {
    required: false,
    description: 'Email used for the Request Access mailto link on LoginPage',
  },
  REACT_APP_LOG_LEVEL: {
    required: false,
    description:
      "Log level: 'silent' | 'error' | 'warn' | 'info' | 'debug' (defaults: 'warn' in prod, 'info' otherwise)",
  },
});

/**
 * Validate environment variables and return a simple config object.
 * Throws a descriptive Error if a required variable is missing.
 */
function validateEnv() {
  const missing = [];

  /** @type {Array<keyof typeof schema>} */
  const keys = /** @type {any} */ (Object.keys(schema));
  for (const key of keys) {
    const spec = schema[key];
    const value = process.env[key];
    if (spec.required && !value) missing.push(key);
  }

  if (missing.length) {
    const detailsStr = missing
      .map((k) => `- ${k}: ${schema[/** @type {keyof typeof schema} */ (k)].description}`)
      .join('\n');
    const example = [
      'Ensure your .env.local contains at least:',
      'REACT_APP_SUPABASE_URL=your_supabase_url',
      'REACT_APP_SUPABASE_ANON_KEY=your_supabase_anon_key',
    ].join('\n');

    const message = ['Missing required environment variables:', detailsStr, '', example].join('\n');

    // Log for visibility. Do not throw in development to avoid blocking the UI.
    logger.error(message);
    if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'test') {
      // In production builds and tests, fail fast so we don't deploy a broken app
      // and so unit tests can assert on this behavior.
      throw new Error(message);
    }
  }

  // Return a narrow config for convenient imports.
  return {
    supabaseUrl: process.env.REACT_APP_SUPABASE_URL || '',
    supabaseAnonKey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
    n8nWebhookUrl: process.env.REACT_APP_N8N_WEBHOOK_URL || '',
    n8nFrontendWebhookUrl: process.env.REACT_APP_N8N_FRONTEND_WEBHOOK_URL || '',
    vapidPublicKey: process.env.REACT_APP_VAPID_PUBLIC_KEY || '',
    accessRequestEmail: process.env.REACT_APP_ACCESS_REQUEST_EMAIL || 'jg@timegenieapp.com',
    // Compute sensible default log level: 'warn' in prod, 'info' otherwise
    // Normalize to lowercase and validate value
    logLevel: (() => {
      const raw = (process.env.REACT_APP_LOG_LEVEL || '').toLowerCase();
      const allowed = new Set(['silent', 'error', 'warn', 'info', 'debug']);
      if (allowed.has(raw)) return raw;
      const isProd = process.env.NODE_ENV === 'production';
      return isProd ? 'warn' : 'info';
    })(),
  };
}

// Perform validation on import (fail-fast) and export the config for convenience.
export const env = validateEnv();

export default env;
